import { Modal } from 'react-bootstrap'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { CaretDown, CaretUp } from 'react-bootstrap-icons'

import TrocasDevolucoesService from '../../../services/database/TrocasDevolucoesService'
import { CustomSpinner } from '../../../components/CustomSpinner'
import { formatDate } from '../../../utils/formatDate'
import { formatPrice } from '../../../utils/formatPrice'
import { useOrderTable } from '../../../hooks/useOrderTable'

import './styles.css'

export function ModalConsultarSaldo({ isOpen, onClose, pessoa }) {
  const [movimentacoes, setMovimentacoes] = useState([])
  const [isMovimentacaoLoading, setIsMovimentacaoLoading] = useState(true)

  const { sortedArray: sortedMovimentacao, check, handleOrganize } =
    useOrderTable(movimentacoes, 'id')

  const loadMovimentacoes = useCallback(async () => {
    setIsMovimentacaoLoading(true)
    try {
      const data = await TrocasDevolucoesService.getAll(pessoa.cod_pessoa)
      setMovimentacoes(data)
    } catch (err) {
      console.error(err)
      toast.error('Erro ao listar as movimentações do cliente!')
    } finally {
      setIsMovimentacaoLoading(false)
    }
  }, [pessoa])

  useEffect(() => { loadMovimentacoes() }, [loadMovimentacoes])

  // Totalizadores
  const totalDevolvido = sortedMovimentacao.reduce((s, m) => s + (m.valor_total ?? 0), 0)
  const totalSaldo = sortedMovimentacao.reduce(
    (s, m) => m.acrescimo_saldo === 'S' ? s + (m.valor_total ?? 0) : s, 0
  )
  const totalItens = sortedMovimentacao.reduce((s, m) => s + (m.quantidade ?? 0), 0)

  return (
    <Modal
      show={isOpen}
      onHide={onClose}
      dialogClassName="modal-consultar-saldo-dialog"
      size="xl"
    >
      {/* Header */}
      <Modal.Header
        closeButton
        style={{ borderBottom: '1px solid #e8e8e8', padding: '1rem 1.25rem' }}
      >
        <div>
          <Modal.Title style={{ fontSize: 16, fontWeight: 600, marginBottom: 2 }}>
            Movimentações de{' '}
            <span style={{ color: '#185FA5' }}>{pessoa?.pes_razao}</span>
          </Modal.Title>
          <p style={{ fontSize: 12, color: '#888', margin: 0 }}>
            {sortedMovimentacao.length} registro{sortedMovimentacao.length !== 1 ? 's' : ''} encontrado{sortedMovimentacao.length !== 1 ? 's' : ''}
          </p>
        </div>
      </Modal.Header>

      <Modal.Body style={{ padding: '1.25rem' }}>
        {isMovimentacaoLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '2.5rem 0' }}>
            <CustomSpinner color="#006fff" />
          </div>
        ) : sortedMovimentacao.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2.5rem 0', color: '#aaa' }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>📭</div>
            <p style={{ fontSize: 14 }}>Nenhuma movimentação encontrada para este cliente.</p>
          </div>
        ) : (
          <>
            {/* Cards de resumo */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: '1.25rem' }}>
              {[
                { label: 'Movimentações', value: sortedMovimentacao.length, color: '#185FA5' },
                { label: 'Itens devolvidos', value: totalItens, color: '#444' },
                { label: 'Total devolvido', value: formatPrice(totalDevolvido), color: '#A32D2D' },
                { label: 'Saldo em haver', value: formatPrice(totalSaldo), color: '#3B6D11' },
              ].map(({ label, value, color }) => (
                <div key={label} style={{ background: '#f7f7f8', borderRadius: 8, padding: '10px 14px' }}>
                  <div style={{ fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 3 }}>{label}</div>
                  <div style={{ fontSize: 17, fontWeight: 600, color }}>{value}</div>
                </div>
              ))}
            </div>

            {/* Tabela */}
            <div style={{ border: '1px solid #e8e8e8', borderRadius: 8, overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, tableLayout: 'fixed', minWidth: 820 }}>
                <colgroup>
                  <col style={{ width: 55 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 80 }} />
                  <col style={{ width: 55 }} />
                  <col style={{ width: 105 }} />
                  <col style={{ width: 105 }} />
                  <col style={{ width: 75 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 100 }} />
                  <col style={{ width: 85 }} />
                </colgroup>
                <thead>
                  <tr style={{ background: '#f7f7f8', borderBottom: '2px solid #e8e8e8' }}>
                    {[
                      { field: 'id',             label: 'ID' },
                      { field: 'id_pedido',       label: 'Pedido' },
                      { field: null,              label: 'Cliente' },
                      { field: null,              label: 'Qtd',       align: 'center' },
                      { field: 'valor_unitario',  label: 'Vl. Unit.', align: 'right' },
                      { field: 'valor_total',     label: 'Vl. Total', align: 'right' },
                      { field: null,              label: 'Acrésc.',   align: 'center' },
                      { field: 'data_devolucao',  label: 'Data Dev.' },
                      { field: null,              label: 'SKU' },
                      { field: null,              label: 'Funcionário', align: 'center' },
                    ].map(({ field, label, align = 'left' }) => (
                      <th
                        key={label}
                        onClick={field ? () => handleOrganize(field) : undefined}
                        style={{
                          padding: '9px 10px',
                          textAlign: align,
                          fontSize: 11,
                          fontWeight: 600,
                          color: '#555',
                          whiteSpace: 'nowrap',
                          cursor: field ? 'pointer' : 'default',
                          userSelect: 'none',
                          letterSpacing: '0.02em',
                        }}
                      >
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
                          {label}
                          {field && (
                            <span style={{ display: 'inline-flex', flexDirection: 'column', lineHeight: 1 }}>
                              <CaretUp size={8} color={check(field, 'asc')} />
                              <CaretDown size={8} color={check(field, 'desc')} />
                            </span>
                          )}
                        </span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedMovimentacao.map((m, idx) => (
                    <tr
                      key={m.id}
                      style={{
                        background: idx % 2 === 0 ? '#fff' : '#fafafa',
                        borderBottom: '1px solid #f0f0f0',
                        transition: 'background 0.1s',
                      }}
                      onMouseEnter={(e) => { e.currentTarget.style.background = '#f0f4ff' }}
                      onMouseLeave={(e) => { e.currentTarget.style.background = idx % 2 === 0 ? '#fff' : '#fafafa' }}
                    >
                      <td style={td()}>{m.id}</td>
                      <td style={td()}>{m.id_pedido}</td>
                      <td style={td()}>
                        <span style={{ color: '#aaa', fontSize: 11 }}>#{m.id_cliente}</span>
                      </td>
                      <td style={td('center')}>{m.quantidade}</td>
                      <td style={td('right')}>{formatPrice(m.valor_unitario)}</td>
                      <td style={{ ...td('right'), fontWeight: 600 }}>{formatPrice(m.valor_total)}</td>
                      <td style={td('center')}>
                        <span style={{
                          display: 'inline-block',
                          padding: '2px 8px',
                          borderRadius: 20,
                          fontSize: 10,
                          fontWeight: 600,
                          background: m.acrescimo_saldo === 'S' ? '#EAF3DE' : '#f1f1f1',
                          color: m.acrescimo_saldo === 'S' ? '#27500A' : '#888',
                        }}>
                          {m.acrescimo_saldo === 'S' ? 'Sim' : 'Não'}
                        </span>
                      </td>
                      <td style={td()}>{formatDate(m.data_devolucao)}</td>
                      <td style={td()}>
                        <span style={{ fontFamily: 'monospace', fontSize: 11, color: '#555' }}>{m.sku_produto}</span>
                      </td>
                      <td style={td('center')}>
                        <span style={{ color: '#aaa', fontSize: 11 }}>#{m.id_funcionario}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </Modal.Body>

      {/* Footer com totalizador destacado */}
      {!isMovimentacaoLoading && sortedMovimentacao.length > 0 && (
        <Modal.Footer style={{ borderTop: '1px solid #e8e8e8', padding: '0.75rem 1.25rem', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
          <span style={{ fontSize: 12, color: '#888' }}>
            {sortedMovimentacao.length} movimentaç{sortedMovimentacao.length === 1 ? 'ão' : 'ões'} · {totalItens} item{totalItens !== 1 ? 'ns' : ''}
          </span>
          <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#888' }}>
              Total devolvido:{' '}
              <strong style={{ color: '#A32D2D' }}>{formatPrice(totalDevolvido)}</strong>
            </span>
            <span style={{ fontSize: 12, color: '#888' }}>
              Saldo em haver:{' '}
              <strong style={{ color: '#3B6D11' }}>{formatPrice(totalSaldo)}</strong>
            </span>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  )
}

function td(align = 'left') {
  return {
    padding: '8px 10px',
    textAlign: align,
    fontSize: 12.5,
    color: '#222',
    whiteSpace: 'nowrap',
    verticalAlign: 'middle',
  }
}